import * as React from 'react'
import clsx from 'clsx'
import { sx as sxFunc, SxProps } from '../sx'
import { formControlState, useFormControl } from '../FormControl'
import { Box } from '../Box'
import style from './index.module.scss'

export interface FormLabelClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if the color is secondary. */
  colorSecondary: string
  /** State class applied to the root element if `focused={true}`. */
  focused: string
  /** State class applied to the root element if `disabled={true}`. */
  disabled: string
  /** State class applied to the root element if `error={true}`. */
  error: string
  /** State class applied to the root element if `filled={true}`. */
  filled: string
  /** State class applied to the root element if `required={true}`. */
  required: string
  /** Styles applied to the asterisk element. */
  asterisk: string
  /** Styles applied to the root element if `size="small"`. */
  sizeSmall: string
  /** Styles applied to the root element if `size="medium"`. */
  sizeMedium: string
  /** Styles applied to the root element if `size="large"`. */
  sizeLarge: string
  /** Styles applied to the suffix if renderSuffix is passed */
  suffix: string
}

export interface FormLabelProps extends React.HTMLAttributes<HTMLLabelElement> {
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<FormLabelClasses>
  /**
   * The component used for the root node.
   * Either a string to use a HTML element or a component.
   * @default label
   */
  Component?: React.ElementType
  /**
   * The color of the component.
   */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  /**
   * If `true`, the label should be displayed in a disabled state.
   */
  disabled?: boolean
  /**
   * If `true`, the label is displayed in an error state.
   */
  error?: boolean
  /**
   * If `true`, the label should use filled classes key.
   */
  filled?: boolean
  /**
   * If `true`, the input of this label is focused (used by `FormGroup` components).
   */
  focused?: boolean
  /**
   * If `true`, the label will indicate that the `input` is required.
   */
  required?: boolean
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * This prop will render component at the end edge of the label
   */
  renderSuffix?: React.ReactNode
}

export const FormLabel = React.forwardRef<HTMLLabelElement, FormLabelProps>(
  function FormLabel(props, ref) {
    const {
      children,
      className,
      color,
      classes,
      Component = 'label',
      disabled,
      error,
      filled,
      focused,
      required,
      renderSuffix,
      sx,
      ...other
    } = props

    const factorFormControl = useFormControl()
    const fcs = formControlState({
      props: { color, required, focused, disabled, error, filled },
      factorFormControl,
      states: ['color', 'required', 'focused', 'disabled', 'error', 'filled'],
    })

    const hasRenderSuffix = Boolean(renderSuffix)

    const label = (
      <>
        {children}
        {fcs.required && (
          <span
            aria-hidden
            className={clsx(style.asteriskComponent, classes?.asterisk)}
          >
            &thinsp;{'*'}
          </span>
        )}
      </>
    )
    const labelWithSuffix = (
      <>
        <Box Component="span">{label}</Box>
        <Box Component="span" className={clsx(style.suffix, classes?.suffix)}>
          {renderSuffix}
        </Box>
      </>
    )
    return (
      <Component
        className={clsx(
          [style.root, classes?.root],
          fcs.disabled && [style.disabled, classes?.disabled],
          fcs.error && [style.error, classes?.error],
          fcs.filled && [classes?.filled],
          fcs.focused && [classes?.focused],
          hasRenderSuffix && style.labelWithSuffix,
          className,
          sxFunc(sx)
        )}
        ref={ref}
        {...other}
      >
        {hasRenderSuffix ? labelWithSuffix : label}
      </Component>
    )
  }
)
